/**
 * Renders a field value for display (read-only mode).
 *
 * Dispatches to:
 *   - field-display-relation-renderers.tsx  — ticket / user / contact / company
 *   - field-display-scalar-renderers.tsx    — date, boolean, select, tags, multi-user, json, text, etc.
 */

import React from "react";
import {
  renderTicketField,
  renderUserField,
  renderContactField,
  renderCompanyField,
} from "./field-display-relation-renderers";
import { renderFieldDisplayScalar } from "./field-display-scalar-renderers";
import { companyDriveFolderUrl } from "@/lib/integrations/google-drive/customer-root";
import { buildSlackChannelUrl } from "@/lib/integrations/slack/channels/slack-deep-link";
import type { SimpleFieldConfig, FieldValue } from "../types";

export interface RenderFieldDisplayProps {
  fieldId: string;
  fieldConfig: SimpleFieldConfig;
  value: FieldValue;
  showIcon?: boolean;
  className?: string;
  disabled?: boolean;
}

/**
 * Main display renderer — routes to relation or scalar helpers based on fieldId / fieldConfig.type.
 */
export const renderFieldDisplay = ({
  fieldId,
  fieldConfig,
  value,
  showIcon = true,
  className = "",
  disabled = false,
}: RenderFieldDisplayProps): React.ReactNode => {
  const engagementStaleness = Boolean(fieldConfig?.engagementStaleness);

  if ((value === null || value === undefined) && !engagementStaleness) {
    return <div className="text-sm text-muted-foreground italic">Not set</div>;
  }

  // --- Relation fields (resolved by fieldId pattern) ---

  if (fieldId === "ticket_id" || fieldId?.includes("ticket")) {
    const node = renderTicketField(value, showIcon, className, disabled);
    if (node) return node;
  }

  if (
    fieldId === "assigned_to" ||
    fieldId === "user_id" ||
    fieldId?.includes("user") ||
    fieldId?.includes("assign")
  ) {
    const node = renderUserField(value, showIcon, className, disabled);
    if (node) return node;
  }

  // contact_id and customer_id both resolve to SmartContactDisplay
  if (
    fieldId === "contact_id" ||
    fieldId === "customer_id" ||
    fieldId?.includes("contact") ||
    fieldId?.includes("customer")
  ) {
    const node = renderContactField(value, showIcon, className, disabled);
    if (node) return node;
  }

  if (fieldId === "company_id" || fieldId?.includes("company")) {
    const node = renderCompanyField(value, showIcon, className, disabled);
    if (node) return node;
  }

  // Google Drive folder → friendly clickable link (value is the folder id).
  if (fieldId === "google_drive_folder_id") {
    const folderId = typeof value === "string" ? value.trim() : "";
    if (folderId) {
      return (
        <a
          href={companyDriveFolderUrl(folderId)}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-sm text-primary hover:underline inline-flex items-center gap-1 ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          📁 Open folder
        </a>
      );
    }
  }

  // Slack project channel → friendly clickable link (value is the channel id).
  if (fieldId === "slack_channel_id") {
    const url = buildSlackChannelUrl(typeof value === "string" ? value : null);
    if (url) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`text-sm text-primary hover:underline inline-flex items-center gap-1 ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          🗂️ Open channel
        </a>
      );
    }
    return <div className={`text-sm text-muted-foreground ${className}`}>No channel</div>;
  }

  // --- Scalar / type-based rendering ---

  const scalarResult = renderFieldDisplayScalar(value, fieldId, fieldConfig, className);
  if (scalarResult !== undefined) return scalarResult;

  // Fallback
  return <div className={`text-sm ${className}`}>{String(value)}</div>;
};
